import { ArrowRight, Calendar, MessageSquare, Check, Star, Zap, Shield, Sparkles } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlanCard } from '../components/subscription/PlanCard';
import { StripeError } from '../components/subscription/StripeError';
import { GradientText } from '../components/ui/GradientText';
import { STRIPE_PRICES } from '../config/stripe';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../hooks/useStripe';
import { useSubscription } from '../hooks/useSubscription';
import { useTranslation } from '../hooks/useTranslation';

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const { user } = useAuth();
  const { subscription, initializeSubscription, initialized } = useSubscription();
  const { createSubscription, error, clearError, loading } = useStripe();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  // Initialize subscription on load
  useEffect(() => {
    if (user && !initialized) {
      initializeSubscription();
    }
  }, [user, initialized, initializeSubscription]);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const scrollY = window.scrollY || window.pageYOffset;
        const section = sectionRef.current;
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const viewportHeight = window.innerHeight;

        if (
          scrollY + viewportHeight > sectionTop &&
          scrollY < sectionTop + sectionHeight
        ) {
          // Only apply effect when section is in viewport
          const parallaxElements = section.querySelectorAll('.parallax-element');
          parallaxElements.forEach((el) => {
            const element = el as HTMLElement;
            const speed = parseFloat(element.dataset.speed || '0.1');
            const yPos = (scrollY - sectionTop) * speed;
            element.style.transform = `translateY(${yPos}px)`;
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Card hover/tilt effect - optimisé pour plus de fluidité
  useEffect(() => {
    const cards = document.querySelectorAll('.pricing-card');
    
    cards.forEach(card => {
      let rafId: number;
      let lastX = 0;
      let lastY = 0;
      
      const handleMouseMove = (e: MouseEvent) => {
        const cardEl = card as HTMLElement;
        const rect = cardEl.getBoundingClientRect();
        
        // Récupération des coordonnées
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Limiter la fréquence des mises à jour pour améliorer les performances
        if (Math.abs(x - lastX) < 5 && Math.abs(y - lastY) < 5) return;
        lastX = x;
        lastY = y;
        
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          
          // Calculer l'angle de rotation (limité pour éviter des rotations excessives)
          const rotateX = Math.max(-5, Math.min(5, (y - centerY) / 25));
          const rotateY = Math.max(-5, Math.min(5, (centerX - x) / 25));
          
          // Appliquer la transformation avec will-change pour optimiser les performances
          cardEl.style.willChange = 'transform';
          cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translate3d(0, 0, 10px)`;
        });
      };
      
      const handleMouseLeave = () => {
        const cardEl = card as HTMLElement;
        
        cancelAnimationFrame(rafId);
        
        // Transition douce au retour à l'état initial
        cardEl.style.transition = 'transform 0.5s ease-out';
        cardEl.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translate3d(0, 0, 0)';
        
        // Réinitialiser after la transition pour permettre une nouvelle animation fluide
        setTimeout(() => {
          cardEl.style.transition = '';
        }, 500);
      };
      
      // Utiliser la délégation d'événements pour optimiser les performances
      card.addEventListener('mousemove', handleMouseMove as EventListener);
      card.addEventListener('mouseleave', handleMouseLeave);
      
      // Nettoyage propre
      return () => {
        card.removeEventListener('mousemove', handleMouseMove as EventListener);
        card.removeEventListener('mouseleave', handleMouseLeave);
        cancelAnimationFrame(rafId);
      };
    });
  }, []);

  const handleSelectPlan = async (tier: string) => {
    try {
      // Si c'est le plan gratuit, rediriger vers l'inscription sans Stripe
      if (tier === 'free') {
        if (!user) {
          navigate('/signup');
        }
        return;
      }

      // Pour les plans payants, utiliser Stripe
      const priceId = STRIPE_PRICES[tier.toLowerCase() as keyof typeof STRIPE_PRICES][billingPeriod];
      if (!priceId) {
        throw new Error('Plan not available');
      }

      if (!user) {
        localStorage.setItem('selectedPlan', JSON.stringify({
          tier,
          billingPeriod,
          timestamp: Date.now()
        }));
        navigate('/signup');
        return;
      }

      await createSubscription(priceId);
    } catch (err) {
      console.error('Error selecting plan:', err);
    }
  };

  // Fonctionnalités communes à tous les plans
  const commonFeatures = [
    { name: t('pages.pricing.features.ai'), included: true },
    { name: t('pages.pricing.features.design'), included: true },
    { name: t('pages.pricing.features.widget'), included: true },
    { name: t('pages.pricing.features.integrations'), included: true },
    { name: t('pages.pricing.features.gallery'), included: true },
    { name: t('pages.pricing.features.stats'), included: true }
  ];

  // Type pour les fonctionnalités avec limite
  interface Feature {
    name: string;
    included: boolean;
    limit?: number;
  }

  // Pricing cards data with icons and tailored features
  const plans = [
    {
      tier: t('pages.pricing.free'),
      price: 0,
      icon: <Shield className="w-6 h-6 text-violet-400" />,
      description: "Perfect for individuals exploring AI possibilities",
      features: [
        ...commonFeatures,
        { name: t('pages.pricing.features.conversations'), included: true, limit: 15 },
        { name: "Chatbots", included: true, limit: 1 }
      ] as Feature[],
      current: subscription?.tier === 'free',
      color: 'from-violet-600 to-indigo-400',
      recommended: false
    },
    {
      tier: t('pages.pricing.starter'),
      price: 19,
      icon: <Zap className="w-6 h-6 text-cyan-400" />,
      description: "Ideal for startups and small businesses",
      features: [
        ...commonFeatures,
        { name: t('pages.pricing.features.conversations'), included: true, limit: 100 },
        { name: "Chatbots", included: true, limit: 1 }
      ] as Feature[],
      current: subscription?.tier === 'starter',
      color: 'from-indigo-500 to-blue-400',
      recommended: false
    },
    {
      tier: t('pages.pricing.professional'),
      price: 49,
      icon: <Star className="w-6 h-6 text-indigo-400" />,
      description: "Perfect for growing businesses and teams",
      features: [
        ...commonFeatures,
        { name: t('pages.pricing.features.conversations'), included: true, limit: 500 },
        { name: "Chatbots", included: true, limit: 3 }
      ] as Feature[],
      current: subscription?.tier === 'professional',
      color: 'from-violet-600 to-cyan-500',
      recommended: true
    },
    {
      tier: t('pages.pricing.enterprise'),
      price: 99,
      icon: <Sparkles className="w-6 h-6 text-indigo-400" />,
      description: "For organizations requiring maximum capacity",
      features: [
        ...commonFeatures,
        { name: t('pages.pricing.features.conversations'), included: true, limit: 1500 },
        { name: "Chatbots", included: true, limit: 5 }
      ] as Feature[],
      current: subscription?.tier === 'enterprise',
      color: 'from-indigo-600 to-violet-400',
      recommended: false
    }
  ];

  return (
    <div className="min-h-screen w-full bg-black overflow-hidden pb-20" ref={sectionRef}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.15),transparent_70%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,212,191,0.15),transparent_70%)]"></div>
      
      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-gradient-to-r from-violet-500/10 to-transparent blur-3xl parallax-element" data-speed="-0.08"></div>
      <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-gradient-to-r from-cyan-500/10 to-transparent blur-3xl parallax-element" data-speed="0.05"></div>
      <div className="absolute bottom-20 left-1/3 w-72 h-72 rounded-full bg-gradient-to-r from-indigo-500/10 to-transparent blur-3xl parallax-element" data-speed="0.1"></div>
      
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 pt-32">
        {/* Header */}
        <div className="text-center mb-20 relative">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/10 text-sm text-white/80 animate-fade-in">
            <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 animate-pulse"></span>
            Pricing Plans
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight animate-fade-in-up">
            {t('pages.pricing.title')}{' '}
            <GradientText className="bg-gradient-to-r from-violet-500 via-indigo-400 to-cyan-500">
              {t('pages.pricing.subtitle')}
            </GradientText>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {t('pages.pricing.description')}
          </p>

          {/* Billing period selector */}
          <div className="inline-flex items-center bg-white/5 backdrop-blur-xl rounded-full p-1 border border-white/10 transform transition-all hover:border-violet-500/50 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                billingPeriod === 'monthly'
                  ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white shadow-lg shadow-violet-500/20'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('pages.pricing.monthly')}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                billingPeriod === 'yearly'
                  ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white shadow-lg shadow-violet-500/20'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('pages.pricing.yearly')}
              <span className="ml-1 text-xs px-2 py-0.5 bg-violet-500/30 rounded-full">-17%</span>
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
          {plans.map((plan, index) => (
            <div 
              key={plan.tier}
              className={`pricing-card relative bg-white/5 backdrop-blur-xl rounded-2xl border overflow-hidden transition-all duration-500 animate-fade-in-up ${
                plan.current 
                  ? 'border-violet-500 shadow-[0_0_30px_rgba(124,58,237,0.3)]' 
                  : plan.recommended
                  ? 'border-gradient shadow-[0_0_30px_rgba(124,58,237,0.2)] scale-105 z-10'
                  : 'border-white/10 hover:border-white/30'
              }`}
              style={{ 
                animationDelay: `${0.1 + index * 0.1}s`,
                borderImage: plan.recommended ? 'linear-gradient(to right, #8b5cf6, #06b6d4) 1' : ''
              }}
            >
              {/* Card top highlight */}
              <div className={`h-1.5 w-full bg-gradient-to-r ${plan.color}`}></div>
              
              {/* Recommended badge */}
              {plan.recommended && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full text-xs font-semibold text-white shadow-lg">
                  Popular
                </div>
              )}
              
              {/* Current plan badge */}
              {plan.current && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-violet-600 to-cyan-500 rounded-full text-xs font-semibold text-white shadow-lg">
                  Current Plan
                </div>
              )}

              <div className="p-8">
                {/* Plan header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center">
                    {plan.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{plan.tier}</h3>
                </div>

                {/* Plan description */}
                <p className="text-gray-400 text-sm mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline">
                    {billingPeriod === 'yearly' ? (
                      <>
                        <span className="text-5xl font-bold text-white inline-block animate-price-bounce">
                          {Math.round(plan.price * 12 * 0.83)}€
                        </span>
                        <span className="text-gray-400 ml-2">/year</span>
                      </>
                    ) : (
                      <>
                        <span className="text-5xl font-bold text-white inline-block animate-price-bounce">
                          {plan.price}€
                        </span>
                        <span className="text-gray-400 ml-2">/month</span>
                      </>
                    )}
                  </div>
                  {billingPeriod === 'yearly' && plan.price > 0 && (
                    <p className="text-sm text-cyan-400 mt-1">
                      or {Math.round((plan.price * 12 * 0.83) / 12)}€/month
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        feature.included ? 'bg-gradient-to-br from-violet-600/20 to-cyan-500/20' : 'bg-gray-500/20'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          feature.included ? 'text-cyan-400' : 'text-gray-500'
                        }`} />
                      </div>
                      <span className={feature.included ? 'text-gray-200' : 'text-gray-500'}>
                        {feature.name}
                        {feature.limit && (
                          <span className="text-gray-400">
                            {feature.limit === -1 ? ' unlimited' : ` (${feature.limit})`}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Button */}
                <button
                  onClick={() => handleSelectPlan(plan.tier.toLowerCase())}
                  disabled={plan.current}
                  className={`w-full py-3 px-4 rounded-xl transition-all duration-300 font-medium ${
                    plan.current
                      ? 'bg-violet-500/20 text-violet-400 cursor-default'
                      : plan.recommended
                      ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white hover:shadow-lg hover:shadow-violet-600/20 transform hover:-translate-y-1'
                      : 'bg-white/5 backdrop-blur-xl text-white border border-white/10 hover:bg-white/10 hover:border-violet-500/50 transform hover:-translate-y-1'
                  }`}
                >
                  {plan.current ? 'Current Plan' : 'Select Plan'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info section - with 3D card effect */}
        <div className="max-w-4xl mx-auto mb-20 relative">
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-violet-600/30 to-cyan-500/30 blur-xl opacity-70"></div>
          <div className="pricing-card relative bg-black/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shrink-0">
                <MessageSquare className="w-7 h-7 text-violet-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">{t('pages.pricing.conversations.title')}</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {t('pages.pricing.conversations.description')}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all">
                    <h4 className="text-lg font-semibold text-white mb-2">Lead Capture</h4>
                    <p className="text-gray-400 text-sm">Automatically identify and qualify potential customers</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all">
                    <h4 className="text-lg font-semibold text-white mb-2">Multi-language</h4>
                    <p className="text-gray-400 text-sm">Support for all major languages to reach global audience</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 hover:border-violet-500/30 transition-all">
                    <h4 className="text-lg font-semibold text-white mb-2">Analytics</h4>
                    <p className="text-gray-400 text-sm">Detailed insights into user engagement and behavior</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom plan section */}
        <div className="max-w-4xl mx-auto relative">
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-violet-600/20 via-indigo-500/20 to-cyan-500/20 blur-xl opacity-70 animate-pulse-slow"></div>
          <div className="pricing-card relative bg-black/50 backdrop-blur-xl rounded-2xl p-8 border border-white/10 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 flex items-center justify-center shrink-0">
                <Calendar className="w-8 h-8 text-violet-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-3">{t('pages.pricing.custom.title')}</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  {t('pages.pricing.custom.description')}
                </p>
                <a 
                  href="https://calendly.com/martin-botmaker-agency/demo-botmaker-agency-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-xl hover:shadow-lg hover:shadow-violet-600/20 transition-all transform hover:-translate-y-1"
                >
                  {t('pages.pricing.custom.cta')}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full bg-gradient-to-br from-violet-600/10 to-cyan-500/10 blur-2xl"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br from-violet-600/10 to-cyan-500/10 blur-2xl"></div>
          </div>
        </div>

        <p className="text-center text-gray-400 mt-16 mb-10">
          {t('pages.pricing.billing_info')} {billingPeriod === 'monthly' ? t('pages.pricing.monthly').toLowerCase() : t('pages.pricing.yearly').toLowerCase()}.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <StripeError 
          message={error} 
          onClose={clearError} 
        />
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-violet-500 animate-spin"></div>
            <div className="absolute inset-1 rounded-full border-2 border-transparent border-l-cyan-500 animate-spin-slow"></div>
            <div className="absolute inset-2 rounded-full border-2 border-transparent border-r-indigo-500 animate-spin-reverse"></div>
            <div className="absolute inset-3 rounded-full border-2 border-transparent border-b-pink-500 animate-spin-slow-reverse"></div>
            <div className="absolute inset-0 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}